import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ArrowLeft, FileText, Download } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const SpaceFurnitureLayout = () => {
  const navigate = useNavigate();
  
  const handleViewDetails = (title: string) => {
    navigate(`/agency-solutions?search=${encodeURIComponent(title)}`);
  };

  const knowledgeBases = [
    {
      title: "Workplace Modeling: Optimal Workspace Configurations",
      description: "Advanced modeling techniques for creating optimal workspace configurations based on federal requirements",
      type: "Model",
      lastUpdated: "2024-01-18"
    },
    {
      title: "Scenario Testing: Open Office vs Hybrid Strategies",
      description: "Comparative analysis tools for testing different office layout strategies and their effectiveness",
      type: "Analysis",
      lastUpdated: "2024-02-22"
    },
    {
      title: "Automated Space Planning: GSA Standards Implementation",
      description: "Automated tools for space planning that comply with GSA standards and regulations",
      type: "Tool",
      lastUpdated: "2024-01-28"
    },
    {
      title: "Generative Furniture Design: Modular & Ergonomic Solutions",
      description: "AI-powered furniture design tools for creating modular and ergonomic workspace solutions",
      type: "Design Tool",
      lastUpdated: "2024-02-15"
    },
    {
      title: "Furniture Reconfiguration & Re-use Guidelines",
      description: "Guidelines and best practices for reconfiguring and reusing existing furniture in federal facilities",
      type: "Guide",
      lastUpdated: "2024-01-20"
    }
  ];

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-6">
          <Link to="/gsa-channel" className="inline-flex items-center text-primary hover:text-primary/80 mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to GSA Channel
          </Link>
          <h1 className="text-3xl font-bold mb-2">Space &/or Furniture Layout</h1>
          <p className="text-muted-foreground">
            Comprehensive tools and frameworks for optimal workspace configurations, furniture planning, and layout optimization in federal buildings.
          </p>
        </div>

        <div className="grid gap-6">
          {knowledgeBases.map((kb, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-2">{kb.title}</CardTitle>
                    <p className="text-muted-foreground">{kb.description}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {kb.type}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Updated: {kb.lastUpdated}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Button variant="outline" size="sm" onClick={() => handleViewDetails(kb.title)}>
                    <FileText className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default SpaceFurnitureLayout;